import { operatorsJson } from "@corca/data";

import type {
  ClosingSymbol,
  ConstantsJsonItem,
  DecoratorTypeValue,
  FunctionsJsonItem,
  LabelNodePosition,
  NodeJson,
  OpeningSymbol,
  OperatorSymbolsJsonItem,
  OperatorTypeValue,
} from "@corca/data";

export function inlineNode(...nodes: NodeJson[]): NodeJson {
  return {
    type: "inline",
    props: {},
    children: nodes,
  };
}

export function numberNode(value: string): NodeJson {
  return {
    type: "number",
    props: { value: value },
    children: [],
  };
}

export function encodeDecoratedName(baseName: string, decoratorNames: string[]): string {
  if (decoratorNames.length === 0) return baseName;
  const prefix = decoratorNames.map((d) => `__dec_${d}__`).join("");
  return `${prefix}${baseName}`;
}

export function decodeDecoratedName(encodedName: string): {
  baseName: string;
  decorators: string[];
} {
  const decorators: string[] = [];
  let remaining = encodedName;

  const decoratorPattern = /^__dec_([^_]+)__/;
  let match = remaining.match(decoratorPattern);

  while (match) {
    decorators.push(match[1]);
    remaining = remaining.slice(match[0].length);
    match = remaining.match(decoratorPattern);
  }

  return {
    baseName: remaining,
    decorators,
  };
}

export function variableNode(name: string): NodeJson {
  const { baseName, decorators } = decodeDecoratedName(name);

  // Parse superscript label (__sup_<text>) before splitting subscript
  let nameWithoutSup = baseName;
  let supLabel: string | undefined;
  const supIndex = baseName.indexOf("__sup_");
  if (supIndex !== -1) {
    nameWithoutSup = baseName.slice(0, supIndex);
    supLabel = baseName.slice(supIndex + "__sup_".length);
  }

  const [varName, label] = nameWithoutSup.split("_");

  // very basic label parsing, just to support x_1, y_2, z_a, etc.
  const labelNodes: NodeJson[] = label
    ? [
        {
          type: "label",
          props: { position: "right-bottom" },
          children: [
            inlineNode(
              Number.isNaN(Number(label))
                ? variableNode(label)
                : {
                    type: "number",
                    props: { value: label },
                    children: [],
                  },
            ),
          ],
        },
      ]
    : [];

  if (supLabel) {
    labelNodes.push({
      type: "label",
      props: { position: "right-top" },
      children: [
        inlineNode(
          Number.isNaN(Number(supLabel))
            ? variableNode(supLabel)
            : {
                type: "number",
                props: { value: supLabel },
                children: [],
              },
        ),
      ],
    });
  }

  const decoratorNodes: NodeJson[] = decorators.map((decoratorName) => ({
    type: "decorator",
    props: { name: decoratorName as DecoratorTypeValue },
    children: [],
  }));

  return {
    type: "variable",
    props: {},
    children: [
      {
        type: "symbol",
        props: { value: varName },
        children: [...labelNodes, ...decoratorNodes],
      },
    ],
  };
}

export function constantNode(json: ConstantsJsonItem): NodeJson {
  return {
    type: "constant",
    props: {
      name: json.name,
      ...json.props,
    },
    children: [
      {
        type: "symbol",
        props: {
          value: json.symbol,
          key: json.symbolProps?.key,
        },
        children: [],
      },
    ],
  };
}

export function operatorSymbolNode(json: OperatorSymbolsJsonItem): NodeJson {
  return {
    type: "operatorSymbol",
    props: {
      name: json.name,
      ...json.props,
    },
    children: [
      {
        type: "symbol",
        props: {
          value: json.symbol,
          key: json.key,
        },
        children: [],
      },
    ],
  };
}

export function functionNode(
  json: FunctionsJsonItem,
  symbolChildren: NodeJson[],
  ...args: NodeJson[]
): NodeJson {
  const { arguments: _slots, ...props } = json.props ?? {};

  return {
    type: "function",
    props: {
      name: json.name,
      key: json.key,
      ...props,
    },
    children: [
      {
        type: "symbol",
        props: {
          value: json.symbol,
          ...json.symbolProps,
        },
        children: symbolChildren,
      },
      ...args,
    ],
  };
}

export function labelNode(position: LabelNodePosition, ...args: NodeJson[]): NodeJson {
  return {
    type: "label",
    props: { position },
    children: args,
  };
}

export function matrixNode(rows: number, cols: number, cells: NodeJson[]): NodeJson {
  return {
    type: "matrix",
    props: {
      rows,
      cols,
    },
    children: cells,
  };
}

export function operatorNode(operatorType: OperatorTypeValue, children: NodeJson[] = []): NodeJson {
  const json = operatorsJson[operatorType];
  if (
    operatorType === "fraction" ||
    operatorType === "root" ||
    operatorType === "power" ||
    operatorType === "factorial"
  ) {
    return {
      type: "operator",
      props: {
        operatorType,
        key: json.key,
      },
      children,
    };
  }

  return {
    type: "operator",
    props: {
      operatorType,
      key: json.key,
    },
    children: [
      {
        type: "symbol",
        props: {
          value: json.symbol,
        },
        children,
      },
    ],
  };
}

export function groupNode(
  openingSymbol: OpeningSymbol | undefined,
  closingSymbol: ClosingSymbol | undefined,
  children: NodeJson[],
): NodeJson {
  return {
    type: "group",
    props: {
      openingSymbol,
      closingSymbol,
    },
    children,
  };
}

export function plainTextNode(value: string): NodeJson {
  return { type: "plainText", props: { value }, children: [] };
}
